import { useMemo } from 'react';

import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import { useAuthToken } from '@/renderer/utils/useMe';

// Non-future pending — matches the My Results pending section (futures excluded).
const NON_FUTURE_PLAY_TYPES = ['side', 'total', 'prop', 'prop-group'] as const;

/**
 * Small "N pending" badge for the My Results nav item — the count of the
 * member's ungraded, non-future plays across all sports. Renders nothing when
 * there are none (or when logged out).
 */
export function PendingCountBadge() {
  const authToken = useAuthToken();
  const loggedIn = !!authToken;

  const request = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'navPendingCount',
        req: '/api/v1/messageReceipt/me',
        query: {
          page: { limit: 100, offset: 0 },
          data: {
            type: 'release',
            result: 'pending',
            playType: [...NON_FUTURE_PLAY_TYPES],
          },
        },
        skipPoll: true,
        pause: !loggedIn,
      }),
      [loggedIn],
    ),
  );

  const count = request.data?.pageInfo?.total ?? request.data?.data?.length ?? 0;

  if (!loggedIn || !count) {
    return null;
  }

  return (
    <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-amber-400/90 px-3 py-2 text-xs font-medium leading-none text-zinc-900">
      {count}
    </span>
  );
}
